import type { AriaAttributes, ReactNode, Ref, RefObject } from 'react'
import { type HTMLAttributeAnchorTarget } from 'react'
import { NavLink as RouterLink, type To } from 'react-router'
import styles from './Link.module.css'
import {
  mergeProps,
  useFocusRing,
  useHover,
  useLink,
  useObjectRef,
} from 'react-aria'
import type { PressEventHandler } from '@/infra/prop-types'
import cx from 'classnames'
import { match } from '@/utils/pattern-match-util'
import { isHoverSupported } from '@/utils/browser-util'

type LinkVariant = 'default' | 'white' | 'navigation' | 'inherit'

interface CommonLinkProps {
  ref?: Ref<HTMLAnchorElement | HTMLSpanElement>
  isDisabled?: boolean
  children: ReactNode | ((props: LinkRenderProps) => ReactNode)
  target?: HTMLAttributeAnchorTarget
  onPress?: PressEventHandler
  testId?: string
  inert?: boolean
  'aria-label'?: string
  'aria-current'?: AriaAttributes['aria-current']
  'aria-labelledby'?: string
}

interface RouterLinkProps {
  to: To
  replace?: boolean
  preventScrollReset?: boolean
}

interface BrowserLinkProps {
  href?: string
}

export type StyledLinkProps = (RouterLinkProps | BrowserLinkProps) &
  CommonLinkProps & {
    variant?: LinkVariant
  }

/**
 * An anchor tag with styling that integrates with React Router, as well as functions as a
 * regular link. Use {@link href} for external navigation, and {@link to} for
 * internal navigation using the Router.
 */
export function StyledLink({
  ref,
  children,
  variant = 'default',
  ...rest
}: StyledLinkProps) {
  const classes = ({
    isDisabled,
    isHovered,
    isPressed,
    isFocusVisible,
    isFocused,
  }: LinkRenderProps) =>
    cx(
      styles.link,
      rest.onPress && !isDisabled && styles.pressable,
      match(variant, {
        default: styles.default,
        white: styles.white,
        navigation: styles.navigation,
        inherit: styles.inherit,
      }),
      isDisabled && styles.disabled,
      isPressed && styles.pressed,
      isHovered && styles.hover,
      isFocused && styles.focus,
      isFocusVisible && styles.focusVisible,
    )

  return (
    <RawLink ref={ref} {...rest} className={classes}>
      {children}
    </RawLink>
  )
}

export interface LinkRenderProps {
  isDisabled: boolean
  isPressed: boolean
  isHovered: boolean
  isFocused: boolean
  isFocusVisible: boolean
  isActive: boolean
}

export type RawLinkProps = (RouterLinkProps | BrowserLinkProps) &
  CommonLinkProps & {
    className?: string | ((props: LinkRenderProps) => string)
  }

/**
 * An anchor tag that integrates with React Router, as well as functions as a
 * regular link. Use {@link href} for external navigation, and {@link to} for
 * internal navigation using the Router.
 */
export function RawLink({
  ref,
  testId,
  isDisabled,
  className,
  ...rest
}: RawLinkProps) {
  isDisabled ??= false

  const { focusProps, isFocused, isFocusVisible } = useFocusRing()

  // NOTE: Not using the Link component from react-aria-components because
  // it doesn't play nice with React Router.
  const refObject = useObjectRef(ref)
  const { linkProps, isPressed } = useLink(
    {
      isDisabled: isDisabled,
      ...rest,
    },
    refObject,
  )

  const { hoverProps, isHovered: _isHovered } = useHover({
    isDisabled: isDisabled,
  })
  const isHovered = _isHovered && isHoverSupported

  if ('to' in rest && rest.to) {
    return (
      <RouterLink
        ref={refObject as RefObject<HTMLAnchorElement>}
        {...mergeProps(linkProps, hoverProps, focusProps)}
        to={rest.to}
        replace={rest.replace}
        preventScrollReset={rest.preventScrollReset}
        data-test={testId}
        className={({ isActive }) =>
          getLinkClassnames(className, {
            isActive,
            isDisabled: isDisabled,
            isFocused,
            isFocusVisible,
            isHovered,
            isPressed,
          })
        }
      >
        {({ isActive }) => {
          const renderProps: LinkRenderProps = {
            isActive,
            isDisabled: isDisabled,
            isFocused,
            isFocusVisible,
            isHovered,
            isPressed,
          }
          return typeof rest.children === 'function'
            ? rest.children(renderProps)
            : rest.children
        }}
      </RouterLink>
    )
  }

  const Component = 'href' in rest && rest.href ? 'a' : 'span'

  const renderProps: LinkRenderProps = {
    isFocused,
    isFocusVisible,
    isHovered,
    isPressed,
    isDisabled: isDisabled,
    isActive: false,
  }

  return (
    <Component
      ref={refObject as RefObject<HTMLAnchorElement>}
      {...mergeProps(linkProps, hoverProps, focusProps)}
      data-test={testId}
      role={Component === 'span' ? 'link' : undefined}
      tabIndex={rest.onPress ? 0 : undefined}
      className={getLinkClassnames(className, renderProps)}
    >
      {typeof rest.children === 'function'
        ? rest.children(renderProps)
        : rest.children}
    </Component>
  )
}

/**
 * Gets the link classname.
 *
 * @param input
 * @param state
 */
function getLinkClassnames(
  input: RawLinkProps['className'],
  state: LinkRenderProps,
) {
  if (!input) {
    return undefined
  }

  if (typeof input === 'string') {
    return input
  }

  return input(state)
}
